import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Send, Brain, Rocket, Save, Gauge, Lightbulb, Target, CalendarClock } from 'lucide-react';
import { api } from '@/lib/api';
import { luxe, PILLARS, gradientCss } from '@/lib/luxe';
import { GlassCard, LuxeButton, Input, Textarea, Field, Spinner, Empty } from '@/components/bits';

const P = PILLARS.marketing;

function Toggle({ on, onChange, label }) {
  return (
    <button onClick={() => onChange(!on)} className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl luxe-btn" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
      <span className="text-sm font-semibold" style={{ color: luxe.text }}>{label}</span>
      <span className="w-10 h-6 rounded-full flex items-center px-0.5 transition-all" style={{ background: on ? gradientCss(P.grad) : luxe.border, justifyContent: on ? 'flex-end' : 'flex-start' }}>
        <span className="w-5 h-5 rounded-full" style={{ background: '#fff' }} />
      </span>
    </button>
  );
}

export default function MyraMarketing() {
  const [view, setView] = useState('command');
  const [profile, setProfile] = useState(null);
  const [autopilot, setAutopilot] = useState(null);
  const [command, setCommand] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api('/marketing/business-profile').then(setProfile).catch(() => setProfile({ exists: false }));
    api('/marketing/autopilot').then(setAutopilot).catch(() => {});
  }, []);

  const runCommand = async () => {
    if (command.trim().length < 3) return toast.error('Tell Myra what you want');
    setBusy(true); setResult(null);
    try {
      const r = await api('/marketing/myra-command', { method: 'POST', body: { command } });
      setResult(r);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveProfile = async () => {
    try { const r = await api('/marketing/business-profile', { method: 'PUT', body: profile }); setProfile(r.profile); toast.success('Business brain updated ✨'); }
    catch (e) { toast.error(e.message); }
  };
  const saveAutopilot = async (next) => {
    setAutopilot(next);
    try { await api('/marketing/autopilot', { method: 'PUT', body: next }); } catch (e) { toast.error(e.message); }
  };
  const setP = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const QUICK = ['Agle 7 din ka content calendar banao', 'Jaipur mein lead campaign plan karo', 'Aaj kya post karun?', 'Meri leads kahan weak hain?', '3 strong content ideas do'];

  return (
    <div className="float-in">
      <GlassCard className="p-5 mb-5" style={{ background: `linear-gradient(135deg, rgba(245,158,11,0.12), rgba(236,72,153,0.08))` }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: gradientCss(P.grad) }}><Brain size={22} color="#2A2005" /></div>
          <div>
            <div className="font-display text-2xl font-black" style={{ color: luxe.text }}>Myra <span style={{ color: P.primary }}>Marketing AI</span></div>
            <div className="text-xs" style={{ color: luxe.textMid }}>Your autonomous marketing department — plans, creates, targets & optimizes.</div>
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          {[['command', 'Command', Sparkles], ['business', 'Business Brain', Brain], ['autopilot', 'Autopilot', Gauge]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setView(id)} className="px-3 py-2 rounded-full text-sm font-bold luxe-btn" style={view === id ? { background: gradientCss(P.grad), color: '#2A2005' } : { background: luxe.surface, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>
              <Icon size={14} className="inline mr-1" />{label}
            </button>
          ))}
        </div>
      </GlassCard>

      {view === 'command' && (
        <>
          <GlassCard className="p-4 mb-4">
            <div className="flex items-end gap-2 p-2 rounded-2xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
              <textarea value={command} onChange={(e) => setCommand(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runCommand(); } }} rows={1} placeholder="Tell Myra what you want to achieve…" data-testid="myra-command-input" className="flex-1 outline-none resize-none bg-transparent text-sm py-2 px-2" style={{ color: luxe.text, maxHeight: 120 }} />
              <button onClick={runCommand} disabled={busy} data-testid="myra-command-send" className="w-10 h-10 rounded-xl flex items-center justify-center luxe-btn shrink-0" style={{ background: gradientCss(P.grad), opacity: busy ? 0.6 : 1 }}>
                {busy ? <Spinner color="#2A2005" /> : <Send size={18} color="#2A2005" />}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {QUICK.map((q) => <button key={q} onClick={() => setCommand(q)} className="px-3 py-1.5 rounded-full text-xs font-semibold luxe-btn" style={{ background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>{q}</button>)}
            </div>
          </GlassCard>

          {busy && <div className="flex items-center gap-2 text-sm py-6 justify-center" style={{ color: luxe.muted }}><Spinner color={P.primary} /> Myra soch rahi hai…</div>}
          {result && (
            <div className="space-y-3" data-testid="myra-result">
              {result.reply && <GlassCard className="p-4"><div className="text-sm" style={{ color: luxe.text }}>{result.reply}</div></GlassCard>}
              {result.missing_info?.length > 0 && (
                <GlassCard className="p-4"><div className="text-[10px] font-extrabold uppercase tracking-widest mb-2" style={{ color: luxe.warning }}>Myra needs to know</div>
                  <ul className="text-sm list-disc pl-4 space-y-1" style={{ color: luxe.textMid }}>{result.missing_info.map((m, i) => <li key={i}>{m}</li>)}</ul></GlassCard>
              )}
              {result.strategy?.length > 0 && (
                <GlassCard className="p-4"><div className="flex items-center gap-2 mb-2"><Target size={15} style={{ color: P.primary }} /><span className="font-bold text-sm" style={{ color: luxe.text }}>Strategy</span></div>
                  <ul className="text-sm list-disc pl-4 space-y-1" style={{ color: luxe.textMid }}>{result.strategy.map((m, i) => <li key={i}>{m}</li>)}</ul></GlassCard>
              )}
              {result.campaign_plan && (
                <GlassCard className="p-4"><div className="flex items-center gap-2 mb-2"><Rocket size={15} style={{ color: P.primary }} /><span className="font-bold text-sm" style={{ color: luxe.text }}>Campaign Plan</span></div>
                  <div className="text-sm space-y-1" style={{ color: luxe.textMid }}>
                    <div><b style={{ color: luxe.text }}>Goal:</b> {result.campaign_plan.objective}</div>
                    <div><b style={{ color: luxe.text }}>Platforms:</b> {(result.campaign_plan.platforms || []).join(', ')}</div>
                    <div><b style={{ color: luxe.text }}>Audience:</b> {result.campaign_plan.audience}</div>
                    <div><b style={{ color: luxe.text }}>Where:</b> {result.campaign_plan.location}</div>
                    <div><b style={{ color: luxe.text }}>Budget:</b> {result.campaign_plan.budget_note}</div>
                    <div style={{ color: luxe.muted }}>{result.campaign_plan.why}</div>
                  </div></GlassCard>
              )}
              {result.content_ideas?.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {result.content_ideas.map((c, i) => (
                    <GlassCard key={i} className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: P.primary }}>{c.platform}</span>
                        {c.best_time && <span className="text-[10px] flex items-center gap-1" style={{ color: luxe.muted }}><CalendarClock size={11} />{c.best_time}</span>}
                      </div>
                      <div className="font-bold text-sm" style={{ color: luxe.text }}>{c.hook}</div>
                      <div className="text-sm mt-1 whitespace-pre-wrap" style={{ color: luxe.textMid }}>{c.caption}</div>
                      {c.hashtags?.length > 0 && <div className="text-xs mt-2" style={{ color: luxe.blue }}>{c.hashtags.join(' ')}</div>}
                    </GlassCard>
                  ))}
                </div>
              )}
              {result.recommendations?.length > 0 && (
                <GlassCard className="p-4"><div className="flex items-center gap-2 mb-2"><Lightbulb size={15} style={{ color: luxe.gold }} /><span className="font-bold text-sm" style={{ color: luxe.text }}>Recommendations</span></div>
                  <ul className="text-sm list-disc pl-4 space-y-1" style={{ color: luxe.textMid }}>{result.recommendations.map((m, i) => <li key={i}>{m}</li>)}</ul></GlassCard>
              )}
              {result.actions?.length > 0 && (
                <GlassCard className="p-4"><div className="text-[10px] font-extrabold uppercase tracking-widest mb-2" style={{ color: luxe.success }}>Next actions</div>
                  <ul className="text-sm list-disc pl-4 space-y-1" style={{ color: luxe.textMid }}>{result.actions.map((m, i) => <li key={i}>{m}</li>)}</ul></GlassCard>
              )}
            </div>
          )}
          {!busy && !result && <Empty icon={Sparkles} title="Ask Myra anything" desc="Set your Business Brain first for sharper, on-brand answers." />}
        </>
      )}

      {view === 'business' && profile && (
        <GlassCard className="p-6">
          <div className="text-sm mb-4" style={{ color: luxe.textMid }}>Fill your business brain once — Myra uses it for every plan, campaign and content.</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Business Name"><Input value={profile.business_name || ''} onChange={setP('business_name')} data-testid="biz-name" /></Field>
            <Field label="Category"><Input value={profile.category || ''} onChange={setP('category')} placeholder="e.g. Electric scooters" /></Field>
            <Field label="Primary Product / Service"><Input value={profile.primary_product || ''} onChange={setP('primary_product')} /></Field>
            <Field label="Price Range"><Input value={profile.price_range || ''} onChange={setP('price_range')} placeholder="₹80k–₹1.2L" /></Field>
            <Field label="Location / City"><Input value={profile.location || ''} onChange={setP('location')} /></Field>
            <Field label="Target Area"><Input value={profile.target_area || ''} onChange={setP('target_area')} placeholder="Jaipur + 40km" /></Field>
            <Field label="Ideal Customer"><Input value={profile.ideal_customer || ''} onChange={setP('ideal_customer')} placeholder="Urban EV buyers 25-45" /></Field>
            <Field label="Unique Selling Point"><Input value={profile.usp || ''} onChange={setP('usp')} /></Field>
            <Field label="Monthly Ad Budget (₹)"><Input type="number" value={profile.monthly_budget || 0} onChange={(e) => setProfile({ ...profile, monthly_budget: +e.target.value })} /></Field>
            <Field label="Objective">
              <select value={profile.objective || 'leads'} onChange={setP('objective')} className="w-full outline-none" style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: luxe.bgElev, color: luxe.text }}>
                {['awareness', 'engagement', 'leads', 'sales', 'traffic'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Brand Voice"><Input value={profile.brand_voice || ''} onChange={setP('brand_voice')} placeholder="Premium, confident, friendly" /></Field>
          <Field label="Current Offers / Promotions"><Textarea rows={2} value={profile.offers || ''} onChange={setP('offers')} /></Field>
          <LuxeButton variant="pillar" color={P} onClick={saveProfile} data-testid="save-biz"><Save size={15} className="inline mr-1" />Save Business Brain</LuxeButton>
        </GlassCard>
      )}

      {view === 'autopilot' && autopilot && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-1"><Gauge size={18} style={{ color: P.primary }} /><span className="font-bold" style={{ color: luxe.text }}>Myra Autopilot</span></div>
          <p className="text-sm mb-4" style={{ color: luxe.muted }}>Control how autonomous Myra is. Paid ad actions stay OFF and require your explicit approval.</p>
          <div className="flex gap-2 mb-4">
            {['off', 'assisted', 'autopilot'].map((m) => (
              <button key={m} onClick={() => saveAutopilot({ ...autopilot, mode: m })} data-testid={`autopilot-${m}`} className="px-4 py-2 rounded-full text-sm font-bold capitalize luxe-btn" style={autopilot.mode === m ? { background: gradientCss(P.grad), color: '#2A2005' } : { background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>{m}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[['organic_posting', 'Organic Posting'], ['auto_scheduling', 'Auto Scheduling'], ['ai_content', 'AI Content Creation'], ['lead_detection', 'Lead Detection'], ['hot_lead_alerts', 'Hot Lead Alerts'], ['campaign_recommendations', 'Campaign Recommendations'], ['auto_optimization', 'Auto Optimization'], ['paid_ads_auto', 'Paid Ads Auto Actions']].map(([k, label]) => (
              <Toggle key={k} label={label} on={!!autopilot[k]} onChange={(v) => saveAutopilot({ ...autopilot, [k]: v })} />
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <Field label="Daily Ad Spend Limit (₹)"><Input type="number" value={autopilot.daily_ad_limit || 0} onChange={(e) => saveAutopilot({ ...autopilot, daily_ad_limit: +e.target.value })} /></Field>
            <Field label="Monthly Ad Spend Limit (₹)"><Input type="number" value={autopilot.monthly_ad_limit || 0} onChange={(e) => saveAutopilot({ ...autopilot, monthly_ad_limit: +e.target.value })} /></Field>
          </div>
        </GlassCard>
      )}
    </div>
  );
}
